import { Alert, Box } from '@mui/material';
import documentIcon from '@assets/misc-icons/document.svg';
import { FileUploadState } from './states';
import { useState, useEffect } from 'react';

export interface DocumentDetectionReport {
  cropped?: { status: boolean, confidence_percent: number };
  smudgy?: { status: boolean, confidence_percent: number };
  blurry?: { status: boolean, confidence_percent: number };
  dimension_issue?: { status: boolean, confidence_percent: number };
  reason?: string;
  showWarning?: boolean;
  [key: string]: any;
}

interface WarningMessageProps {
  fileUploadState: FileUploadState;
  error: string;
  documentDetectionReport: DocumentDetectionReport | null;
  allowedFileTypes: string[] | undefined;
}

export default function WarningMessage(props: WarningMessageProps) {
  const { fileUploadState, error, allowedFileTypes, documentDetectionReport } = props;
  const [docDetectMsg, setDocDetectMsg] = useState('');
  const generateWarningMessage = () => {
    let message = '';
    const { blurry, smudgy, cropped, dimension_issue } = documentDetectionReport || {};

    if (blurry?.status || smudgy?.status || cropped?.status || dimension_issue?.status) {
      message = message + 'Uploaded file may be';
    } else {
      message = message + 'Uploaded file may be';
    }
    if (blurry?.status) {
      message = message + ' blurry';
    }
    if (smudgy?.status) {
      message = message + ' smudged';
    }
    if (cropped?.status) {
      message = message + ' cropped';
    }
    if (dimension_issue?.status) {
      message = message + ' having dimensional or layout issues';
    }
    if (
      documentDetectionReport &&
      !blurry?.status &&
      !smudgy?.status &&
      !cropped?.status &&
      !dimension_issue?.status
    ) {
      message = message + ' may have an issue. Please check and try again.';
    } else {
      message = message + '.';
    }
    return message;
  };
  useEffect(() => {
    setDocDetectMsg(generateWarningMessage());
  }, [documentDetectionReport]);
  const allowedTypesMessage = Array.isArray(allowedFileTypes)
    ? allowedFileTypes
        .map((type: string) => {
          const parts = type.split('/');
          return parts[1] ? parts[1] : type;
        })
        .join(', ')
    : '';
  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          my: 2
        }}>
        <Box
          component="img"
          src={documentIcon}
          alt="Document Icon"
          sx={{
            width: { xs: '100px', sm: '150px', md: '190px' },
            height: { xs: '100px', sm: '150px', md: '190px' },
            display: 'block',
            margin: '0 auto',
            maxWidth: '100%'
          }}
        />
      </Box>
      <Box
        component="h5"
        sx={{
          fontSize: { xs: '1.5rem', sm: '1.6rem', md: '1.8rem' },
          textAlign: 'center',
          fontWeight: 600,
          mb: 1
        }}>
        {fileUploadState === FileUploadState.WARNING
          ? 'Document Not Clear — Upload Again to Proceed'
          : error}
      </Box>
      <Box
        sx={{
          fontSize: { xs: '1.4rem', sm: '1.5rem', md: '1.6rem' },
          mb: '1.4rem'
        }}>
        {fileUploadState === FileUploadState.WARNING ? (
          <ul>
            <li>{docDetectMsg}</li>
            <li>Upload a clear, full-sized scanned copy.</li>
            <li>Make sure all text and seals are visible.</li>
          </ul>
        ) : (
          <ul>
            <li>The file you uploaded isn’t supported by our system.</li>
            <li>{`Only  ${allowedTypesMessage} formats are allowed.`}</li>
            <li>Make sure the file opens correctly on your device before uploading.</li>
          </ul>
        )}

        {documentDetectionReport?.reason && (
          <Alert
            severity="warning"
            sx={{
              color: '#7A4F01',
              backgroundColor: '#FFF8E1',
              fontWeight: '400',
              display: 'flex',
              alignItems: 'center',
              flexDirection: 'row',
              mt: 2
            }}>
            {documentDetectionReport?.reason}
            <br />
          </Alert>
        )}
      </Box>
    </>
  );
}
